import { useParams, Navigate, Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { Leaf, Droplets, Wrench, ArrowRight, Phone, MapPin, ArrowUpRight, Check, Star, Quote } from "lucide-react";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { COMMUNE_BY_SLUG, COMMUNES, slugify, normalize } from "@/data/communes";
import { NEIGHBOURS } from "@/data/communeNeighbours";
import { CITY_CONTENT } from "@/data/cityContent";
import { CONTACT } from "@/data/site";
import { api } from "@/lib/api";

// Tous les mots-clés métier, regroupés par catégorie (chaque item devient
// "<mot-clé> à <commune>" pour cibler les recherches locales).
const SERVICE_GROUPS = [
  {
    key: "jardins", title: "Parcs & Jardins", to: "/parcs-jardins", icon: Leaf,
    items: [
      "Jardinier", "Entretien de jardins", "Entretien de parcs", "Tonte de pelouse",
      "Débroussaillage", "Taille de haies et d'arbustes", "Entretien de végétation",
      "Désherbage", "Création d'espaces verts", "Aménagement paysager",
      "Création de massifs", "Restauration de jardins", "Remise en état de jardins",
      "Entretien régulier des espaces verts", "Évacuation des déchets verts",
    ],
  },
  {
    key: "nettoyage", title: "Nettoyage extérieur", to: "/nettoyage-exterieur", icon: Droplets,
    items: [
      "Nettoyage extérieur", "Nettoyage de façade", "Nettoyage de terrasse",
      "Nettoyage d'allées et de cours", "Nettoyage haute pression",
      "Traitement anti-mousse", "Traitement hydrofuge",
      "Remise en état des surfaces extérieures",
    ],
  },
  {
    key: "reparations", title: "Petites réparations", to: "/petites-reparations", icon: Wrench,
    items: ["Petites réparations", "Petits travaux extérieurs", "Interventions diverses"],
  },
];

export default function LocalCommune() {
  const { slug } = useParams();
  const commune = COMMUNE_BY_SLUG[slug];
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    if (!commune) return;
    api.get("/reviews")
      .then(({ data }) => setReviews(data.filter((r) => r.commune && normalize(r.commune) === normalize(commune))))
      .catch(() => {});
  }, [commune]);

  if (!commune) return <Navigate to="/zone-intervention" replace />;

  // Communes voisines RÉELLES (géographiques) ; repli alphabétique si absente
  let neighbours = NEIGHBOURS[commune];
  if (!neighbours || neighbours.length === 0) {
    const idx = COMMUNES.indexOf(commune);
    neighbours = [];
    for (let i = 1; neighbours.length < 6 && i < COMMUNES.length; i++) {
      const c = COMMUNES[(idx + i) % COMMUNES.length];
      if (c !== commune) neighbours.push(c);
    }
  }

  const cityText = CITY_CONTENT[commune];

  return (
    <div data-testid="local-commune-page">
      <Seo
        title={`Jardinier & nettoyage extérieur à ${commune} (06) | Sandro Services 06`}
        description={`Sandro Services 06 intervient à ${commune} (Alpes-Maritimes, 06) : entretien de jardins, débroussaillage, taille, nettoyage de façade, traitement anti-mousse et hydrofuge, petites réparations. Demandez votre devis gratuit.`}
      />

      {/* HERO */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 pt-16 md:pt-24 pb-10">
        <Reveal>
          <nav className="text-xs text-ink-soft flex items-center gap-2 flex-wrap" data-testid="breadcrumb">
            <Link to="/" className="hover:text-olive transition-colors">Accueil</Link>
            <span>/</span>
            <Link to="/zone-intervention" className="hover:text-olive transition-colors">Zone d'intervention</Link>
            <span>/</span>
            <span className="text-forest font-medium">{commune}</span>
          </nav>
          <span className="mt-6 inline-flex items-center gap-2 text-sm uppercase tracking-[0.3em] text-olive">
            <MapPin size={15} /> Alpes-Maritimes (06)
          </span>
          <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl text-forest leading-[1.05]">
            Jardinier et nettoyage extérieur à {commune}
          </h1>
          <p className="mt-6 text-lg text-ink-soft max-w-2xl leading-relaxed">
            Sandro Services 06 intervient à <strong className="text-forest">{commune}</strong> et dans tout le
            département des Alpes-Maritimes pour l'entretien de vos parcs et jardins, le nettoyage de vos surfaces
            extérieures et vos petites réparations. Un travail soigné, de proximité, avec devis gratuit.
          </p>
          {cityText && (
            <div className="mt-4 space-y-3 max-w-3xl" data-testid="city-rich-content">
              {cityText.map((para, i) => (
                <p key={i} className="text-base text-ink-soft leading-relaxed">{para}</p>
              ))}
            </div>
          )}
          <div className="mt-8 flex flex-wrap gap-4">
            <Link to="/contact" data-testid="local-devis-btn" className="inline-flex items-center gap-2 bg-olive text-white rounded-full px-7 py-3.5 font-semibold hover:bg-olive-hover transition-all">
              Demander un devis à {commune} <ArrowRight size={18} />
            </Link>
            <a href={`tel:${CONTACT.phone}`} className="inline-flex items-center gap-2 border border-line rounded-full px-7 py-3.5 font-semibold text-forest hover:bg-sand-100 transition-all">
              <Phone size={16} /> {CONTACT.phoneDisplay}
            </a>
          </div>
        </Reveal>
      </section>

      {/* SERVICES LOCAUX */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 py-10 md:py-14">
        <Reveal>
          <h2 className="font-display text-3xl md:text-4xl text-forest">Nos services à {commune}</h2>
          <p className="mt-3 text-ink-soft max-w-3xl">
            Sandro Services 06 propose à {commune} l'ensemble de ses prestations : jardinier, entretien
            d'espaces verts, débroussaillage, taille, création et restauration de jardins, nettoyage extérieur
            (façades, terrasses, allées), traitement anti-mousse et hydrofuge, ainsi que petites réparations.
          </p>
        </Reveal>
        <div className="mt-8 grid lg:grid-cols-3 gap-6" data-testid="local-services">
          {SERVICE_GROUPS.map((g) => (
            <Reveal key={g.key}>
              <div className="bg-white border border-line rounded-2xl p-6 h-full">
                <Link to={g.to} className="group inline-flex items-center gap-3">
                  <span className="w-11 h-11 rounded-full bg-olive/10 grid place-items-center text-olive shrink-0">
                    <g.icon size={20} />
                  </span>
                  <span className="font-display text-2xl text-forest">{g.title}</span>
                </Link>
                <ul className="mt-5 flex flex-wrap gap-2">
                  {g.items.map((item) => (
                    <li key={item}>
                      <Link
                        to={g.to}
                        className="inline-flex items-center gap-1.5 bg-sand border border-line rounded-full px-3 py-1.5 text-xs text-forest hover:border-olive hover:bg-white transition-colors"
                      >
                        <Check size={12} className="text-olive shrink-0" /> {item} à {commune}
                      </Link>
                    </li>
                  ))}
                </ul>
                <Link to={g.to} className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-olive hover:gap-2.5 transition-all">
                  Découvrir {g.title.toLowerCase()} <ArrowUpRight size={14} />
                </Link>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* AVIS CLIENTS DU SECTEUR */}
      {reviews.length > 0 && (
        <section className="max-w-[1400px] mx-auto px-6 md:px-12 py-10 md:py-14" data-testid="commune-reviews">
          <Reveal>
            <h2 className="font-display text-3xl md:text-4xl text-forest">Avis clients à {commune}</h2>
            <p className="mt-3 text-ink-soft">Ils nous ont fait confiance dans votre secteur.</p>
          </Reveal>
          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {reviews.slice(0, 6).map((r) => (
              <Reveal key={r.id}>
                <div className="bg-white border border-line rounded-2xl p-6 h-full">
                  <Quote size={22} className="text-olive/40" />
                  <p className="mt-3 text-ink-soft text-sm leading-relaxed">« {r.message} »</p>
                  <div className="mt-4 flex items-center justify-between">
                    <span className="font-semibold text-forest text-sm">{r.name}</span>
                    <span className="flex">{[1,2,3,4,5].map((n) => <Star key={n} size={13} className={n <= r.rating ? "fill-olive text-olive" : "text-line"} />)}</span>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      {/* POURQUOI + VOISINES */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 py-10 md:py-14 grid lg:grid-cols-2 gap-12">
        <Reveal>
          <div className="bg-forest text-sand rounded-2xl p-8 grain h-full">
            <h2 className="font-display text-2xl md:text-3xl">Une entreprise locale à {commune}</h2>
            <ul className="mt-6 space-y-3 text-sand/85 text-sm">
              {["Travail soigné et finitions de qualité", "Entretien, nettoyage, restauration et petits travaux", "Entreprise de proximité dans les Alpes-Maritimes", "Devis gratuit et sans engagement"].map((t) => (
                <li key={t} className="flex items-start gap-3"><Check size={16} className="text-olive mt-0.5 shrink-0" /> {t}</li>
              ))}
            </ul>
            <Link to="/contact" className="mt-8 inline-flex items-center gap-2 bg-sand text-forest rounded-full px-6 py-3 font-semibold hover:bg-white transition-all">
              Décrire mon projet <ArrowRight size={16} />
            </Link>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <h2 className="font-display text-2xl md:text-3xl text-forest">Communes voisines</h2>
          <p className="mt-3 text-ink-soft text-sm">Nous intervenons aussi dans les communes proches de {commune} :</p>
          <div className="mt-5 flex flex-wrap gap-2" data-testid="neighbour-communes">
            {neighbours.map((c) => (
              <Link key={c} to={`/zone/${slugify(c)}`} className="inline-flex items-center gap-1.5 bg-white border border-line rounded-full px-4 py-2 text-sm text-forest hover:border-olive transition-colors">
                <MapPin size={13} className="text-olive" /> {c}
              </Link>
            ))}
          </div>
          <Link to="/zone-intervention" className="mt-8 inline-flex items-center gap-2 text-forest font-semibold border-b-2 border-olive pb-1 hover:gap-3 transition-all">
            Voir les 163 communes desservies <ArrowRight size={16} />
          </Link>
        </Reveal>
      </section>
    </div>
  );
}
